import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ABOUT_PROFILE_PHOTO } from '../../../media';

export default function AboutHero() {
  const { t } = useTranslation();

  return (
    <section
      className="relative min-h-screen pt-[72px] flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #D2DAFF 0%, #EEF1FF 45%, #dde4ff 100%)' }}
    >
      {/* Radial glow — top right, coral */}
      <div
        className="absolute -top-24 -right-24 w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(177,178,255,0.22) 0%, rgba(177,178,255,0.07) 50%, transparent 72%)' }}
      />
      {/* Radial glow — bottom left, teal */}
      <div
        className="absolute -bottom-20 -left-20 w-[440px] h-[440px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(170,196,255,0.45) 0%, rgba(170,196,255,0.10) 55%, transparent 75%)' }}
      />
      {/* Yellow accent blob — mid right */}
      <div
        className="absolute top-1/3 right-[8%] w-[180px] h-[180px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(210,218,255,0.35) 0%, transparent 70%)' }}
      />
      {/* Dot-grid texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, #1E2A5E 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* Top accent line */}
      <div
        className="absolute top-[72px] left-0 right-0 h-px pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(170,196,255,0.40), transparent)' }}
      />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12 py-20 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
          {/* Left Content */}
          <div className="flex-1 lg:max-w-[55%]">
            <div className="mb-6 flex flex-col gap-1">
              <span className="text-xs uppercase tracking-[0.2em] text-sage font-inter font-medium">
                {t('about.hero.badge').split('\n')[0]}
              </span>
              <span className="text-xs text-sage/70 font-inter font-light italic tracking-wide normal-case">
                {t('about.hero.badge').split('\n')[1]}
              </span>
            </div>
            <h1 className="font-cormorant text-[1.65rem] md:text-[1.9rem] lg:text-[2.35rem] font-light text-charcoal leading-[1.25] mb-8">
              {t('about.hero.heading')}
            </h1>
            <p className="text-base md:text-lg text-charcoal/65 font-inter font-light leading-relaxed mb-10 max-w-[480px]">
              {t('about.hero.intro')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="whitespace-nowrap inline-flex items-center justify-center px-7 py-3.5 rounded-md bg-beige text-white text-sm font-inter font-medium hover:bg-beige/85 transition-colors duration-200 cursor-pointer"
              >
                {t('about.hero.cta1')}
              </Link>
              <Link
                to="/services"
                className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md border border-sage/40 text-charcoal text-sm font-inter font-medium hover:border-sage hover:text-sage transition-all duration-200 cursor-pointer"
              >
                {t('about.hero.cta2')}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>

            {/* Stats strip */}
            <div className="mt-14 pt-10 border-t border-sage/20 flex flex-wrap gap-8">
              {[
                { value: '20+', labelKey: 'about.hero.stat1' },
                { value: '3',   labelKey: 'about.hero.stat2' },
                { value: 'IGA', labelKey: 'about.hero.stat3' },
              ].map((s) => (
                <div key={s.labelKey}>
                  <p className="font-cormorant text-4xl font-semibold text-beige">{s.value}</p>
                  <p className="text-xs uppercase tracking-widest text-text-muted font-inter mt-1">{t(s.labelKey)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Image */}
          <div className="flex-1 w-full lg:max-w-[45%] relative">
            <div
              className="absolute -inset-4 rounded-3xl pointer-events-none"
              style={{ background: 'linear-gradient(135deg, rgba(170,196,255,0.22) 0%, rgba(210,218,255,0.15) 100%)' }}
            />
            <div className="relative rounded-2xl overflow-hidden w-full" style={{ paddingBottom: '125%' }}>
              <img
                src={ABOUT_PROFILE_PHOTO}
                alt="Mirela, Clinical Group Analyst"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sage/20 to-transparent pointer-events-none" />
            </div>
            <Link
              to="/contact"
              className="absolute -bottom-5 -left-5 bg-canvas border border-sage/30 rounded-xl px-5 py-4 hover:bg-beige hover:border-beige transition-all duration-200 cursor-pointer group"
              style={{ backdropFilter: 'blur(8px)' }}
            >
              <p className="text-xs uppercase tracking-widest text-sage group-hover:text-white/80 font-inter font-medium transition-colors duration-200">{t('about.hero.locationLabel')}</p>
              <p className="text-sm font-inter font-medium text-charcoal group-hover:text-white mt-0.5 transition-colors duration-200">{t('about.hero.locationValue')}</p>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
